<template>
  <div class="wrapper-center-content">
    <div class="card login-card d-flex flex-column align-items-center p-5">
      <img src="/greenbow-logo.png" alt="Greenbow" class="login-logo mb-4" />
      <div class="align-text-center mb-6">
        <div class="mb-1">{{ $t('Login.title.first') }}</div>
        <div class="weight-semibold">{{ $t('Login.title.second') }}</div>
      </div>
      <form class="login-form" @submit.prevent="submit">
        <AtomInput
          v-if="!isFirstStepDone && !isRateLimited"
          v-model="inputText"
          class="mb-4"
          data-ph-no-capture
          :placeholder="$t('Login.placeholder')"
          :auto-complete="getCredentialAutocomplete()"
          :error-message="inputError"
          @blur="handleInputBlur" />

        <AtomAlert
          v-if="!isFirstStepDone && isRateLimited && rateLimitAlert"
          :alert="rateLimitAlert"
          show-icon />

        <div
          v-else-if="isFirstStepDone"
          class="w-full d-flex flex-column align-items-center">
          <AtomButton
            appearance="unstyled"
            class="function-link align-self-start mb-3"
            :is-disabled="isRateLimited"
            @click="goBackToCredentialInput">
            <template #text>
              <span>←</span>
              {{ $t('Login.backButton') }}
            </template>
          </AtomButton>

          <AtomAlert
            v-if="isRateLimited && rateLimitAlert"
            :alert="rateLimitAlert"
            show-icon />

          <VerificationCodeInput
            v-else
            ref="verificationInput"
            v-model="verificationCode"
            :email="displayEmail"
            :error="verificationError"
            :disabled="isRateLimited"
            @complete="handleVerificationComplete"
            @resend="handleResend" />
        </div>

        <AtomButton
          v-if="!isFirstStepDone"
          :action-text="$t('Login.button')"
          :is-disabled="!inputText.length || !!inputError || isRateLimited"
          :is-loading="loading"
          class="mx-auto"
          @click="submit" />

        <AtomButton
          v-if="!isFirstStepDone && !$config.public.isProd"
          appearance="tertiary"
          class="mt-6"
          :action-text="$t('Login.debug.resetButton')"
          @click="debugReset" />
      </form>
      <transition name="fade">
        <AtomIcon v-if="isLoginConfirmed" icon="checkmark-green" class="mt-4" />
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { AutocompleteTypes } from '~/types/enums/autocomplete.enum';
import { emailRegex, phoneValidatorRegex } from '~/utils/regex';
import { RouteName } from '~/types/enums/routes.enum';
import { InfoAlert } from '~/types/models/alert.model';

definePageMeta({
  layout: 'content-only'
});
const authStore = useAuthStore();
const { t } = useI18n();
const isFirstStepDone = ref(false);
const inputText = ref('');
const verificationCode = ref('');
const verificationError = ref('');
const displayEmail = ref('');
const loading = ref(false);
const isLoginConfirmed = ref(false);
const isRateLimited = ref(false);
const rateLimitCountdown = ref(0);
const rateLimitType = ref<'login_attempts' | 'ip_attempts' | null>(null);
const rateLimitAlert = ref<InfoAlert | null>(null);
const profileStore = useProfileStore();
// const loading = ref(true)

const verificationInput = ref<any>(null);
const inputError = ref<string | null>(null);

watch(inputText, () => {
  inputError.value = null;
});

function isValidCredential(value: string): boolean {
  const cleaned = value.replace(/\s/g, '');
  return emailRegex.test(cleaned) || phoneValidatorRegex.test(cleaned);
}

function handleInputBlur() {
  const value = inputText.value.trim();
  if (value.length > 0 && !isValidCredential(value)) {
    inputError.value = t('Auth.error.invalidCredentials');
  } else {
    inputError.value = null;
  }
}

function getInputValue() {
  return inputText.value.trim();
}

const headers = useRequestHeaders(['cookie']);

async function createLogin() {
  // Check rate limit first
  const rateLimitStatus = authStore.checkRateLimit();
  if (rateLimitStatus.isBlocked) {
    // Update UI to show current rate limit
    isRateLimited.value = true;
    rateLimitCountdown.value = rateLimitStatus.timeRemaining;
    rateLimitType.value =
      (rateLimitStatus.type as 'login_attempts' | 'ip_attempts') ||
      'login_attempts';
    createRateLimitAlert(
      rateLimitStatus.timeRemaining,
      rateLimitStatus.type || 'login_attempts'
    );
    if (!countdownInterval) {
      startRateLimitCountdown();
    }
    return;
  }

  // @ts-ignore
  document.activeElement.blur();

  if (authStore.initialized) {
    await authStore.resetState();
  }

  if (await authStore.createLogin(getInputValue())) {
    inputText.value = '';
    verificationCode.value = '';
    verificationError.value = '';
    displayEmail.value = await authStore.getCredentialDisplay();
    isFirstStepDone.value = true;
  }
}

async function confirmLogin() {
  verificationError.value = '';

  const result = await authStore.confirmLogin(verificationCode.value);

  if (result.success) {
    const { logError } = useNuxtApp();
    isLoginConfirmed.value = true;

    await new Promise((resolve) => setTimeout(resolve, 500));

    await authStore.initialize({ cookie: headers.cookie });
    const redirectPath =
      sessionStorage.getItem('redirectAfterLogin') ||
      RouteName.DASHBOARD_CHARGERS_OVERVIEW;
    sessionStorage.removeItem('redirectAfterLogin');

    const localeRoute = useLocaleRoute();
    const route = localeRoute(redirectPath);
    profileStore.fetchProfile();
    if (route) {
      const router = useRouter();
      router.push(route.fullPath).catch((error) => {
        logError(error as Error, 'Login page', 'Navigation error:');
      });
    }
  } else if (result.isRateLimited && result.rateLimitData) {
    // Handle rate limiting in UI
    const { retryAfter, type } = result.rateLimitData;
    isRateLimited.value = true;
    rateLimitCountdown.value = retryAfter;
    rateLimitType.value = type;

    // Create InfoAlert with countdown
    createRateLimitAlert(retryAfter, type);

    // Start countdown timer
    startRateLimitCountdown();

    // Clear verification code
    verificationCode.value = '';
    verificationInput.value?.clearCode();
  } else {
    verificationError.value = t('Login.error.invalidCode');
    verificationCode.value = '';
    verificationInput.value?.clearCode();
  }
}

async function submit() {
  if (loading.value) return;
  loading.value = true;
  await (isFirstStepDone.value ? confirmLogin() : createLogin());
  loading.value = false;
}

function handleVerificationComplete(code: string) {
  verificationCode.value = code;
  submit();
}

async function handleResend() {
  verificationCode.value = '';
  verificationError.value = '';
  await authStore.resendLogin();
}

function goBackToCredentialInput() {
  isFirstStepDone.value = false;
  verificationCode.value = '';
  verificationError.value = '';
  displayEmail.value = '';

  // Reset rate limiting
  isRateLimited.value = false;
  rateLimitCountdown.value = 0;
  rateLimitType.value = null;
  rateLimitAlert.value = null;

  // Focus the email/phone input after going back
  nextTick(() => {
    const inputElement = document.querySelector('.login-form input');
    if (inputElement instanceof HTMLElement) {
      inputElement.focus();
    }
  });
}

let countdownInterval: NodeJS.Timeout | null = null;

function createRateLimitAlert(retryAfter: number, type: string) {
  const titleKey =
    type === 'ip_attempts'
      ? 'Login.rateLimit.title.ipAttempts'
      : 'Login.rateLimit.title.loginAttempts';
  const title = t(titleKey);
  const message = t('Login.rateLimit.message', {
    time: formatCountdown(retryAfter)
  });

  rateLimitAlert.value = new InfoAlert(crypto.randomUUID(), title, message);
}

function updateRateLimitAlert() {
  if (rateLimitAlert.value && rateLimitCountdown.value > 0) {
    const titleKey =
      rateLimitType.value === 'ip_attempts'
        ? 'Login.rateLimit.title.ipAttempts'
        : 'Login.rateLimit.title.loginAttempts';
    const title = t(titleKey);
    const message = t('Login.rateLimit.message', {
      time: formatCountdown(rateLimitCountdown.value)
    });

    rateLimitAlert.value.title = title;
    rateLimitAlert.value.text = message;
  }
}

function startRateLimitCountdown() {
  // Clear any existing countdown
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }

  countdownInterval = setInterval(() => {
    rateLimitCountdown.value--;

    // Update alert message with new countdown
    updateRateLimitAlert();

    if (rateLimitCountdown.value <= 0) {
      // Rate limit expired
      isRateLimited.value = false;
      rateLimitType.value = null;
      rateLimitAlert.value = null;
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
    }
  }, 1000);
}

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return t('Login.countdown.zeroSeconds');

  if (seconds < 60) {
    return `${seconds} ${seconds !== 1 ? t('Login.countdown.seconds') : t('Login.countdown.second')}`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (remainingSeconds === 0) {
    return `${minutes} ${minutes !== 1 ? t('Login.countdown.minutes') : t('Login.countdown.minute')}`;
  }

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Check for existing rate limit on mount
onMounted(() => {
  const rateLimitStatus = authStore.checkRateLimit();
  if (rateLimitStatus.isBlocked) {
    isRateLimited.value = true;
    rateLimitCountdown.value = rateLimitStatus.timeRemaining;
    rateLimitType.value =
      (rateLimitStatus.type as 'login_attempts' | 'ip_attempts') ||
      'login_attempts';

    createRateLimitAlert(
      rateLimitStatus.timeRemaining,
      rateLimitStatus.type || 'login_attempts'
    );
    startRateLimitCountdown();
  }
});

// Cleanup on unmount
onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});

function getCredentialAutocomplete() {
  // Detect if current input looks like email or phone
  const currentValue = inputText.value.trim();

  if (currentValue.includes('@')) {
    return AutocompleteTypes.Email;
  } else if (currentValue.length > 0 && /^\+?\d/.test(currentValue)) {
    return AutocompleteTypes.TelNational;
  }

  // Default to email for empty input (most common)
  return AutocompleteTypes.Email;
}

// Debug function to reset login state
function debugReset() {
  const { $logInfo } = useNuxtApp();
  $logInfo('DEBUG: Resetting login state...', 'Login Page');
  authStore.debugResetLogin();

  // Reset UI state
  isFirstStepDone.value = false;
  inputText.value = '';
  verificationCode.value = '';
  verificationError.value = '';
  displayEmail.value = '';
  isRateLimited.value = false;
  rateLimitCountdown.value = 0;
  rateLimitType.value = null;
  rateLimitAlert.value = null;

  // Clear countdown timer
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }

  $logInfo('DEBUG: Login state reset complete!', 'Login Page');
}

useHead({
  title: () => t('Login.meta.title'),
  titleTemplate: (title: any) => `${title} | Greenbow`
});
</script>
<style lang="scss" scoped>
.login-logo {
  height: 56px;
  width: auto;
}

.login-card {
  background-color: $color-gray-100;
  width: 100%;
  max-width: 40rem;
}

.login-form {
  width: 100%;
}

.align-self-start {
  align-self: flex-start;
}
</style>
